import Checkbox from "@app/components/checkbox";
import { ConsentActionType, PathnameUrl } from "@app/constants";
import ConsentLogService from "@app/services/http/consent.log.service";
import AccountService from "@app/services/http/account.service";
import { CreateConsentLogPayload } from "@app/types";
import useObservable from "@core/hooks/use-observable.hook";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { openPortalDialog } from "@app/services/modal.service";
import ConfirmModal from "@app/components/confirm-modal";

export default function PhoneVerificationConsent() {
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [isEnableBtnVerify, setIsEnableBtnVerify] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string>("0988 666 999");

  const navigate = useNavigate();
  const { subscribeUntilDestroy } = useObservable();

  // Kiểm tra thông tin người dùng khi vào trang
  useEffect(() => {
    subscribeUntilDestroy(AccountService.getMyInfo(), (res) => {
      if (res) {
        if (res.phone) {
          setPhoneNumber(res.phone);
        }

        if (res.isVerifyPhone === 1) {
          openPortalDialog(ConfirmModal, {
            message: "Số điện thoại của bạn đã được xác minh thành công!",
          });
          navigate(PathnameUrl.PROFILE);
        }
      }
    });
  }, []);

  // Hàm lưu log xác nhận đồng ý
  const logConsent = (action: ConsentActionType, actionDescription: string) => {
    const body: CreateConsentLogPayload = {
      action: action,
      actionDescription: actionDescription,
    };

    subscribeUntilDestroy(ConsentLogService.createConsentLog(body), (res) => {
      if (res.action === ConsentActionType.CHECKED_VERIFY_PHONE) {
        setIsEnableBtnVerify(true);
      } else if (res.action === ConsentActionType.ACCEPTED_VERIFY_PHONE) {
        navigate(PathnameUrl.VERIFY_PHONE);
      }
    });
  };

  // Xử lý khi chọn/bỏ chọn checkbox
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();

    const checked = e.target.checked;

    setIsChecked(checked);

    if (checked) {
      logConsent(
        ConsentActionType.CHECKED_VERIFY_PHONE,
        "Đã đọc và đồng ý điều khoản xử lý số điện thoại để xác minh tài khoản."
      );
    }
  };

  // Xử lý khi nhấn nút Xác minh
  const handleVerifyPhone = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!isChecked) return;

    logConsent(
      ConsentActionType.ACCEPTED_VERIFY_PHONE,
      `Đồng ý xác minh số điện thoại ${phoneNumber}`
    );
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 max-w-xl w-full p-6 md:p-8">
        {/* Tiêu đề chính */}
        <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-6 border-b border-slate-100 pb-3">
          Xác minh số điện thoại
        </h2>

        {/* Khung hiển thị Số điện thoại */}
        <div className="mb-5">
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            Số điện thoại:
          </label>
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-800 font-semibold text-lg">
            <span>{phoneNumber}</span>
            <span className="inline-flex items-center text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
              ⚠️ Chưa xác thực
            </span>
          </div>
        </div>

        {/* Nội dung diễn giải mục đích sử dụng */}
        <p className="text-slate-600 text-sm leading-relaxed mb-6">
          Số điện thoại được sử dụng để xác minh thông tin tài khoản, tăng cường
          bảo mật và phòng chống hành vi gian lận/lạm dụng dịch vụ.
        </p>

        {/* Checkbox Đồng ý điều khoản */}
        <div className="flex items-start gap-3 mb-6 bg-slate-50/50 p-3.5 rounded-lg border border-slate-100">
          <Checkbox
            id="phone-consent-checkbox"
            checked={isChecked}
            onChange={handleCheckboxChange}
            className="mt-1 h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer accent-blue-600 shrink-0"
          />
          <label
            htmlFor="phone-consent-checkbox"
            className="text-xs md:text-sm text-slate-700 leading-snug cursor-pointer select-none"
          >
            Tôi đã đọc và đồng ý với{" "}
            <a
              href="#"
              className="text-blue-600 underline font-medium hover:text-blue-700"
            >
              Điều khoản sử dụng
            </a>
            , bao gồm việc{" "}
            <strong className="font-semibold text-slate-800">PCRENDER</strong>{" "}
            xử lý số điện thoại của tôi nhằm mục đích xác minh thông tin tài
            khoản và bảo đảm an toàn dịch vụ.
          </label>
        </div>

        {/* Nút Thực hiện xác minh */}
        <button
          onClick={handleVerifyPhone}
          disabled={!isChecked || !isEnableBtnVerify}
          className={`w-full font-bold py-3.5 px-4 rounded-lg transition-all duration-200 shadow-md text-base ${
            isChecked && isEnableBtnVerify
              ? "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer active:scale-[0.99]"
              : "bg-blue-300 text-white/80 cursor-not-allowed"
          }`}
        >
          XÁC MINH SỐ ĐIỆN THOẠI
        </button>
      </div>
    </div>
  );
}
